package nl.volleyanalyzer.web.controller

import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import kotlinx.datetime.format
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.*
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import nl.volleyplanner.api.sports.CompetitionType
import nl.volleyplanner.api.sports.MatchStatus
import nl.volleyplanner.api.volleyanalyzer.PouleMatchesResponse
import nl.volleyplanner.web.*
import nl.volleyplanner.web.base.*
import nl.volleyplanner.web.base.component.TabPaneElement
import nl.volleyplanner.web.base.component.tabPane
import nl.volleyplanner.web.util.BootstrapBreakPoint
import nl.volleyplanner.web.util.BootstrapUtil
import org.w3c.dom.*

class PouleMatchesComponent(private val selectedPoule: String, private val controlsFlow: MutableStateFlow<PouleOverviewControls>) : HTMLComponent() {
    private val pouleMatchesFlow = PouleMatchesDataManager.subscribeToPoule(selectedPoule)

    override fun render(consumer: TagConsumer<HTMLElement>) : HTMLElement {
        val controls = controlsFlow.value
        if (pouleMatchesFlow.value == null) {
            return consumer.div { spinner() }
        }
        val pouleMatches = pouleMatchesFlow.value!!
        return consumer.div {
            h5 { +"Wedstrijden" }
            controls.focusTeams.let {
                if (it.isNotEmpty()) {
                    span { +"Filter: ${it.sorted().joinToString(", ") }"}
                }
            }
            pouleMatches.pouleMatchesResponse.matches
                .filter { controls.focusTeams.isEmpty() || controls.focusTeams.contains(it.teamHome) || controls.focusTeams.contains(it.teamGuest) }
                .sortedBy { it.startDateTime }
                .forEach { match ->
                    div (classes = "row py-4 border-top border-secondary-subtle bg-secondary-subtle bg-opacity-10 position-relative") {
                        if (match.matchStatus == MatchStatus.Cancelled) {
                            classes += "text-decoration-line-through"
                        }

                        div(classes = "col-2") {
                            +(match.startDateTime?.date?.format(DATE_TEXT_NO_YEAR_SHORT_DAY_FORMAT) ?: "ntb")
                        }
                        div(classes = "col-3 text-end") {
                            if (controls.focusTeams.contains(match.teamHome)) classes += "fw-bolder"
                            val lastRoundPosition = pouleMatches.standings?.roundStandings?.values?.last()?.records?.keys?.indexOf(match.teamHome)

                            +"${match.teamHome}${lastRoundPosition?.let { " (${it + 1})" } ?: ""}"
                        }
                        div(classes = "col-1 text-center") {
                            if (match.hasScore()) {
                                +"${match.setsA} - ${match.setsB}"
                            } else {
                                +(match.startDateTime?.time?.format(LOCAL_TIME_TIME_FORMAT) ?: "ntb")
                                if (match.matchStatus == MatchStatus.Cancelled && match.matchStatusReason != null) {
                                    br {}
                                    span { strong { +match.matchStatusReason!! } }
                                }
                            }
                        }
                        div (classes = "col-4") {
                            if (controls.focusTeams.contains(match.teamGuest)) classes += "fw-bolder"
                            val lastRoundPosition = pouleMatches.standings?.roundStandings?.values?.last()?.records?.keys?.indexOf(match.teamGuest)

                            +"${match.teamGuest}${lastRoundPosition?.let { " (${it + 1})" } ?: ""}"
                        }
                        div (classes = "col-2 text-end") {
                            a (classes = "stretched-link link-secondary link-underline link-underline-opacity-0") {
                                href = "/poule/$selectedPoule/match/${match.id}"
                                +">"
                            }
                        }
                    }
                }
        }
    }

    override suspend fun getState() = controlsFlow
}

class PouleStandingsComponent(private val pouleMatches: PouleMatchesData, private val pouleOverviewControls: MutableStateFlow<PouleOverviewControls>, private val wideView: Boolean) : HTMLComponent() {

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val round = pouleOverviewControls.value.selectedRound
        val standings = pouleMatches.standings!!.roundStandings.getValue(round)
        return consumer.div(classes = "") {
            h5 { +"Stand (ronde $round)" }
            div (classes = "row text-bg-secondary py-2") {
                div (classes = "col-1") { +"#" }
                div (classes = "col-7") {
                    if (wideView) classes += "col-md-3"
                    +"Team"
                }
                div (classes = "col-2") { +"Wedstr" }
                div (classes = "col-2") { +"Punten" }
                if (wideView) {
                    div(classes = "d-none d-md-block col-md-2") { +"Sets Voor" }
                    div(classes = "d-none d-md-block col-md-2") { +"Sets Tegen" }
                }
            }
            var anyCupTournament = false
            standings.records.values.forEachIndexed { index, teamStanding ->
                div (classes = "row py-2") {
                    div (classes = "col-1") {
                        strong { +"${index + 1}" }
                    }
                    div (classes = "col-7") {
                        if (wideView) classes += "col-md-3"
                        style = "cursor: pointer;"
                        if (pouleOverviewControls.value.focusTeams.contains(teamStanding.team)) {
                            classes += "fw-bold"
                        }

                        onClickFunction = { event ->
                            event.preventDefault()
                            val controls = pouleOverviewControls.value
                            if (controls.focusTeams.contains(teamStanding.team)) {
                                pouleOverviewControls.value = controls.copy(focusTeams = controls.focusTeams - teamStanding.team)
                            } else {
                                pouleOverviewControls.value = controls.copy(focusTeams = controls.focusTeams + teamStanding.team)
                            }
                        }

                        +teamStanding.team
                        // Display a cup trophy in case the current poule is not a cup tournament but the team also participates in one
                        if (pouleMatches.pouleMatchesResponse.fullPoule.competitionType != CompetitionType.CupTournament &&
                            pouleMatches.pouleMatchesResponse.teams.getValue(teamStanding.team).poules.any { it.competitionType == CompetitionType.CupTournament }) {
                            anyCupTournament = true
                            icon("bi-trophy", IconPosition.End)
                        }
                    }
                    div (classes = "col-2") {
                        +teamStanding.matchesPlayed.toString()
                    }
                    div (classes = "col-2") {
                        +teamStanding.points.toString()
                    }
                    if (wideView) {
                        div (classes = "d-none d-md-block col-md-2") {
                            +teamStanding.setsWon.toString()
                        }
                        div (classes = "d-none d-md-block col-md-2") {
                            +teamStanding.setsLost.toString()
                        }
                    }
                }
            }
            if (anyCupTournament) {
                div(classes = "row mt-2") {
                    div(classes = "col") {
                        icon("bi-trophy")
                        span { +"Neemt ook deel aan bekertoernooi" }
                    }
                }
            }
        }
    }

    override suspend fun getState() = pouleOverviewControls
}

data class PouleComponentControls(
    val displayComponent: HTMLComponent?
)

data class PouleOverviewControls(
    val selectedRound: Int,
    val focusTeams: Set<String> = emptySet()
)

class PouleOverviewComponent(private val selectedPoule: String) : HTMLComponent() {
    private val logger = Logger.getLogger("nl.volleyanalyzer.web.controller.PouleOverviewComponent")

    private val pouleMatches = PouleMatchesDataManager.subscribeToPoule(selectedPoule)
    private val pouleOverviewControls = MutableStateFlow<PouleOverviewControls>(PouleOverviewControls(pouleMatches.value?.standings?.roundStandings?.keys?.max() ?: 0))

    private var _pouleOverviewControlsCollectorJob: Job? = null

    private var currentChartComponent: PouleStandingsChartComponent? = null

    override fun onDispose() {
        super.onDispose()
        _pouleOverviewControlsCollectorJob?.cancel()
    }

    override fun onReady() {
        super.onReady()

        _pouleOverviewControlsCollectorJob?.cancel()
        _pouleOverviewControlsCollectorJob = CoroutineScope(Dispatchers.Default).launch {
            pouleOverviewControls.collect { controls ->
                currentChartComponent?.focusTeams(controls.focusTeams)
            }
        }
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        if (pouleMatches.value == null) {
            return consumer.div { spinner() }
        }
        val pouleMatchesResponse = pouleMatches.value?.pouleMatchesResponse
        val standings = pouleMatches.value?.standings
        currentChartComponent = standings?.let { PouleStandingsChartComponent(it) }
        return consumer.div {
            if (pouleMatchesResponse != null) {
                h3 { +pouleMatchesResponse.fullPoule.name }
                div(classes = "row mb-2") {
                    +"Laatste update: ${pouleMatchesResponse.fullPoule.lastFullSync.format(DATE_TIME_FORMAT)}"
                }
            }
            if (standings != null) {
                div(classes = "row") {
                    div(classes = "col text-center mb-3") {
                        span(classes = "me-2") {
                            +"Speelronde"
                        }
                        div(classes = "btn-group me-2") {
                            standings.roundStandings.keys.forEach { round ->
                                input(classes = "btn-check") {
                                    id = "round-selector-input-$round"
                                    type = InputType.radio
                                    attributes["name"] = "standings-view-round-selector"
                                    checked = round == pouleOverviewControls.value.selectedRound
                                    onChangeFunction = { event ->
                                        event.preventDefault()
                                        if (round != pouleOverviewControls.value.selectedRound) {
                                            pouleOverviewControls.value =
                                                pouleOverviewControls.value.copy(selectedRound = round)
                                        }
                                    }
                                }
                                label(classes = "btn btn-outline-secondary") {
                                    htmlFor = "round-selector-input-$round"
                                    +"$round"
                                }
                            }
                        }
                    }
                }
                div(classes = "row d-lg-none") {
                    div(classes = "col text-center mb-3") {
                        div(classes = "btn-group") {
                            input(classes = "btn-check") {
                                id = "standings-view-button-chart"
                                type = InputType.radio
                                attributes["name"] = "standings-view-component"
                                checked = true
                                onChangeFunction = { event ->
                                    event.preventDefault()
                                    val chartComponent = document.getElementById("standings-view-chart-component")!!
                                    val tableComponent = document.getElementById("standings-view-table-component")!!
                                    when ((event.target as HTMLInputElement).checked) {
                                        true -> {
                                            chartComponent.removeClass("d-none")
                                            tableComponent.addClass("d-none")
                                        }

                                        false -> {
                                            chartComponent.addClass("d-none")
                                            tableComponent.removeClass("d-none")
                                        }
                                    }
                                }
                            }
                            label(classes = "btn btn-outline-secondary") {
                                htmlFor = "standings-view-button-chart"
                                +"Grafiek"
                            }
                            input(classes = "btn-check") {
                                id = "standings-view-button-table"
                                type = InputType.radio
                                attributes["name"] = "standings-view-component"
                                onChangeFunction = { event ->
                                    event.preventDefault()
                                    val chartComponent = document.getElementById("standings-view-chart-component")!!
                                    val tableComponent = document.getElementById("standings-view-table-component")!!
                                    when ((event.target as HTMLInputElement).checked) {
                                        true -> {
                                            chartComponent.addClass("d-none")
                                            tableComponent.removeClass("d-none")
                                        }

                                        false -> {
                                            chartComponent.removeClass("d-none")
                                            tableComponent.addClass("d-none")
                                        }
                                    }
                                }
                            }
                            label(classes = "btn btn-outline-secondary") {
                                htmlFor = "standings-view-button-table"
                                +"Tabel"
                            }
                        }
                    }
                }
            }

            div (classes = "row mb-3") {
                // TODO: Better fix than padding top to let chart start together with standings
                div (classes = "col-12 col-lg-6 d-lg-block") {
                    id = "standings-view-chart-component"
                    if (standings != null) {
                        simpleComponent(currentChartComponent!!)
                    }
                }
                div (classes = "col-12 col-lg-6 d-none d-lg-block") {
                    id = "standings-view-table-component"
                    if (standings != null) {
                        val isWideView = when {
                            BootstrapUtil.getCurrentBreakpoint() <= BootstrapBreakPoint.MD -> true
                            // TODO: button to make poule view wide
                            else -> false
                        }
                        component(PouleStandingsComponent(pouleMatches.value!!, pouleOverviewControls, isWideView))
                    } else {
                        p (classes = "p-5") { +"Kan momenteel geen stand berekenen" }
                    }
                }
            }
            logger.debug("Creating matches component: $selectedPoule, ${pouleOverviewControls.value}")
            tabPane(
                "poule-overview-tabs",
                listOf(
                    TabPaneElement(
                        tabTitle = "Wedstrijden",
                        tabHtmlId = "matches-tab",
                        contentHtmlId = "matches-content",
                        component = PouleMatchesComponent(selectedPoule, pouleOverviewControls)
                    ),
                    TabPaneElement(
                        tabTitle = "Vergelijken",
                        tabHtmlId = "comparison-tab",
                        contentHtmlId = "comparison-content",
                        component = PouleResultsComparisonComponent(pouleMatches.value!!)
                    )
                )
            )
        }
    }

    override suspend fun getState() = pouleMatches
}

class MainPouleComponent(private val selectedPoule: StateFlow<String?>) : HTMLComponent() {

    override fun onStateChange() {
        super.onStateChange()
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        return consumer.div {
            if (selectedPoule.value == null) {
                spinner()
            } else {
                component(PouleOverviewComponent(selectedPoule.value!!))
            }
        }
    }

    override suspend fun getState() = selectedPoule
}

data class PouleMatchesData(
    val pouleMatchesResponse: PouleMatchesResponse
) {
    val standings = pouleMatchesResponse.calculatePouleStandings()
}